import React, { Suspense, lazy } from "react";
import { Link, Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { BottomNavigationAction, CircularProgress, Container } from '@mui/material';

import { AccessAlarm, AutoGraphOutlined, AutoStories, BarChart, CarRentalOutlined, Category, DateRange, DirectionsCarOutlined, GraphicEq, Group, GroupsOutlined, Image, PublishedWithChanges, School, Settings, StarOutlineSharp, SupervisorAccount, TrackChanges, UploadFile, WarningAmber } from '@mui/icons-material';

import moment from 'moment';
import 'moment/locale/fr';
import { useSelector } from 'react-redux';
import AppDrawer from './components/AppDrawer';
import Page from './components/Page';

import { useTranslation } from 'react-i18next';
import Page404 from './Page404';
import UsageStatisticsView from './UsageStatistics/UsageStatisticsView';
import Part3DViewer from './Part3DViewer/Part3DViewer';
import BomPart3DManager from "./BomPart3DManagement/BomPart3DManager";
import Demo from "./Demo";


const LazyUserListView = lazy(() => import('./UsersManager/UserListView'));
const LazySettingsView = lazy(() => import('./Settings/SettingsView'));
const LazyConfigureTotp = lazy(() => import('./Settings/ConfigureTotp'));
const LazySearchPage = lazy(() => import('./ImageSearch/SearchImagePage'));
const LazySearchExtractDatePage = lazy(() => import('./SearchExtractDatePage'));
const LazySearchNoticeOfChange = lazy(() => import('./SearchNoticeOfChange'));
const LazyProgressUpdate = lazy(() => import('./Progress/ProgressUpdate'));
const LazyProgressViewer = lazy(() => import('./Progress/ProgressViewer'));
const LazyStatisticsView = lazy(() => import('./Statistics/StatisticsView'));
const LazyGroupsView = lazy(() => import('./UsersManager/PermissionGroupView'));
const ManualImportPage = lazy(() => import('./Admin/BulkImportPage'));
const LazyLexicon = lazy(() => import('./Lexicon/LexiconClient/Lexicon'));
const LazyStatusStatisticsView = lazy(() => import('./Statistics/StatusStatisticsView'));
const LazyAccessRulesPage = lazy(() => import("./UsersManager/AccessRules/AccessRulesPage"));
const LazySkuGroupsView = lazy(() => import("./UsersManager/SkuGroups/SkuGroupView"));
const LazyVideoTutorialView = lazy(() => import("./Tutorials/TutorialsView"));

moment.locale('fr')

const MainApp = (props) => {

    const { t, i18n } = useTranslation();

    const user = useSelector(state => state.user.user)

    if (!user) {
        return <Container maxWidth="lg" style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
        </Container>
    }

    const links = [
    ]

    if (user?.all_permissions.includes("can_search_image")) {
        links.push({ text: t('Image'), icon: <Image />, path: '/search/image', type: 'link' })
    }
    if (user?.all_permissions.includes("can_search_by_system_number")) {
        links.push({ text: t('Exportation Date'), icon: <DateRange />, path: '/search/system-number', type: 'link' })
    }
    {
        user?.all_permissions.includes("can_search_notice_of_change") && links.push({ text: t('Notice of Change'), icon: <WarningAmber />, path: '/search/notice-of-change', type: 'link' })
    }
    if (user?.all_permissions.includes("can_read_progress")) {
        links.push({ text: t('Progress'), icon: <TrackChanges />, path: '/progress', type: 'link' })
    }
    if (user?.all_permissions.includes("can_manage_progress")) {
        links.push({ text: t('Edit Progress'), icon: <PublishedWithChanges />, path: '/update-progress', type: 'link' })
    }
    if (user?.all_permissions.includes("can_read_lexicon")) {
        links.push({ text: t('Glossary'), icon: <AutoStories />, path: '/glossary', type: 'link' })
    }
    if (user?.all_permissions.includes("can_read_statistics")) {
        links.push({ text: t('Statistics'), icon: <AutoGraphOutlined />, path: '/statistics', type: 'link' })
    }
    if (user?.all_permissions.includes("can_read_part_status")) {
        links.push({ text: t('Part Status'), icon: <StarOutlineSharp />, path: '/part-status', type: 'link' })
    }
    if (user.role === "admin") {
        links.push({
            text: t("Permissions"), icon: <GroupsOutlined />, path: '/permissions', type: 'menu', elements: [
                { text: t("User Groups"), icon: <GroupsOutlined />, path: '/permissions/groups', type: 'link' },
                { text: t("SKU Groups"), icon: <DirectionsCarOutlined />, path: '/permissions/sku-groups', type: 'link' },
                { text: t("Access Rules"), icon: <Category />, path: '/permissions/access-rules', type: 'link' }
            ]
        })
    }

    if (user.role === "admin") {
        links.push({ text: t("Users"), icon: <SupervisorAccount />, path: '/users', type: 'link' })
        links.push({ text: t("Analytics"), icon: <BarChart />, path: '/analytics', type: 'link' })
    }

    if (user?.all_permissions.includes("can_manage_import")) {
        links.push({ text: t("import.bulk.title"), icon: <UploadFile />, path: '/bulk-import', type: 'link' })
    }
    links.push({ text: t("Tutorials"), icon: <School />, path: "/tutorials", type: "link" })
    links.push({ text: t("Settings"), icon: <Settings />, path: '/settings', type: 'link' })

    return (
        <Router>
            <AppDrawer links={links} user={user} logoff={props.logoff} changeScope={props.changeScope}>
                <LocalizationProvider adapterLocale={'fr'} dateAdapter={AdapterMoment}>
                    <Suspense fallback={<Page>
                        <Container maxWidth="lg" style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <CircularProgress />
                        </Container>
                    </Page>}>
                        <Routes>
                            <Route path="/" element={<Navigate to="/search/image" />}>
                            </Route>
                            {
                                user?.all_permissions.includes("can_search_image") && <Route path="/search/image" element={<LazySearchPage />} />
                            }
                            {
                                user?.all_permissions.includes("can_search_by_system_number") && <Route path="/search/system-number" element={<LazySearchExtractDatePage />} />
                            }
                            {
                                user?.all_permissions.includes("can_search_notice_of_change") && <Route path="/search/notice-of-change" element={<LazySearchNoticeOfChange />} />
                            }

                            {
                                user?.all_permissions.includes("can_manage_progress") && <Route path="/update-progress" element={<LazyProgressUpdate />} />
                            }
                            {
                                user?.all_permissions.includes("can_read_progress") && <Route path="/progress" element={<LazyProgressViewer />} />
                            }
                            {
                                user.role === "admin" && <Route path="/users" element={<LazyUserListView />} />
                            }
                            {
                                user.role === "admin" && <Route path="/permissions/groups" element={<LazyGroupsView />} />
                            }
                            {
                                user.role === "admin" && <Route path="/permissions/sku-groups" element={<LazySkuGroupsView />} />
                            }
                            {
                                user?.all_permissions.includes("can_read_part_status") && <Route path="/part-status" element={<LazyStatusStatisticsView />} />
                            }
                            {
                                user?.all_permissions.includes("can_read_statistics") && <Route path="/statistics" element={<LazyStatisticsView />} />
                            }
                            <Route path="/statistics" element={<LazyStatisticsView />} />
                            {
                                user?.all_permissions.includes("can_read_lexicon") && <>
                                    <Route path="/glossary" element={<LazyLexicon />} />
                                    <Route path="/glossary/:serieName?/:systemName?/" element={<LazyLexicon />} />
                                    <Route path="/glossary/:serieName?/:systemName?/:partId?/" element={<LazyLexicon />} />

                                </>
                            }
                            {
                                user.role === "admin" && <Route path="/analytics" element={<UsageStatisticsView />} />
                            }

                            <Route path="/part3d" element={<Part3DViewer partNumber={"503192494"} />} />

                            {
                                user?.all_permissions.includes("can_manage_import") && <Route path="/bulk-import" element={<ManualImportPage />} />
                            }

                            <Route path="/settings" element={<LazySettingsView />} />
                            <Route path="/settings/totp" element={<LazyConfigureTotp />} />
                            {
                                (user?.all_permissions.includes("can_manage_access_rule") || user.role === "admin") && <Route path="/permissions/access-rules" element={<LazyAccessRulesPage />} />
                            }

                            <Route path="/tutorials" element={<LazyVideoTutorialView />} />

                            <Route path="/bom-part-manager" element={<BomPart3DManager />} />
                            {/* 404 */}
                            <Route path="*" element={<Page404 />} />
                        </Routes>
                    </Suspense>
                </LocalizationProvider>
            </AppDrawer>
        </Router>
    )

}

export default MainApp;